export type CompanyVehicleStatus = "active" | "inactive" | "maintenance" | "retired";
export type VehicleFuelLevel = "empty" | "quarter" | "half" | "three_quarters" | "full";

export type VehicleConditionOfficerRef = {
  user_id?: number;
  name?: string | null;
  user_name?: string | null;
};

export type CompanyVehicle = {
  id: number;
  company_vehicle_id?: number;
  unit_number?: string | null;
  /** Display name (API); legacy list responses may use `name`. */
  label?: string | null;
  name?: string | null;
  plate?: string | null;
  vin?: string | null;
  year?: number | string | null;
  status?: CompanyVehicleStatus | string | null;
  current_mileage?: number | null;
  property_id?: number | null;
  /** True only when `status` is `active`; used for shift pickers. */
  selectable?: boolean;
  /**
   * When `false`, vehicle has open custody or is otherwise reserved — other officers cannot select it
   * (use with `filter[available_for_assignment]=true` for patrol pickers).
   */
  available_for_assignment?: boolean;
  vehicle_make_id?: number | null;
  vehicle_model_id?: number | null;
  make?: string | null;
  model?: string | null;
  /** Officer with current custody (list/detail from GET `company-vehicles`). */
  officer_custody?: VehicleConditionOfficerRef | null;
  /**
   * Legacy/alternate embed; prefer `officer_custody` when both exist.
   * @deprecated Use `officer_custody`
   */
  current_operator?: VehicleConditionOfficerRef | null;
  /** `1` when DELETE is allowed; `0` otherwise (list/detail from API). */
  can_be_deleted?: 0 | 1 | boolean | "0" | "1";
  created_at?: string | null;
  updated_at?: string | null;
  created_by?: {id?: number; name?: string | null} | null;
};

export type CreateCompanyVehiclePayload = {
  property_id?: number | null;
  vehicle_make_id?: number;
  vehicle_model_id?: number;
  unit_number?: string | null;
  label?: string | null;
  year?: number | null;
  plate?: string | null;
  vin?: string | null;
  status?: CompanyVehicleStatus | null;
  current_mileage?: number | null;
};

export type UpdateCompanyVehiclePayload = Partial<CreateCompanyVehiclePayload>;

/** Canonical maintenance flags for `POST` / `GET` (0–5 items; server dedupes). */
export const VEHICLE_SERVICE_REQUEST_IDS = [
  "oil_change",
  "tire_service",
  "windshield_wiper",
  "windshield_cleaning_fluid",
  "mechanical_issue",
] as const;

export type VehicleServiceRequestId = (typeof VEHICLE_SERVICE_REQUEST_IDS)[number];

/** Body for `POST /vehicle-condition-checks` (Laravel validation names). */
export type VehicleConditionCheckPayload = {
  shift_id: number;
  company_vehicle_id: number;
  phase: "start" | "end";
  mileage: number;
  fuel_level: VehicleFuelLevel;
  /** Preferred; use `[]` when none. Legacy `oil_change_requested` / `tire_service_requested` are not sent. */
  service_requests: VehicleServiceRequestId[];
  notes?: string | null;
  /** Public file URLs from `POST /files` (same pattern as DAR / citations). Omit when none. */
  files?: string[];
};

/** Minimal row for patrol/shift helpers (narrower than list payloads). */
export type VehicleConditionCheckRow = {
  id: number;
  worked_shift_id?: number;
  shift_id?: number;
  company_vehicle_id: number;
  check_type?: "start" | "end";
  phase?: "start" | "end";
  odometer_miles?: number | null;
  mileage?: number | null;
  service_requests?: string[];
  /** Derived for legacy clients; new UI uses `service_requests` only. */
  oil_change_requested?: boolean;
  tire_service_requested?: boolean;
  company_vehicle?: CompanyVehicle;
};

/** Row from GET /vehicle-condition-checks (management list/detail). Matches API payloads. */
export type VehicleConditionCheckListItem = {
  vehicle_condition_check_id: number;
  company_vehicle_id: number;
  shift_id?: number | null;
  worked_shift_id?: number | null;
  user_id?: number | null;
  reviewed_by_user_id?: number | null;
  copied_from_check_id?: number | null;
  company_vehicle?: CompanyVehicle | null;
  officer?: VehicleConditionOfficerRef | null;
  reviewed_by?: VehicleConditionOfficerRef | null;
  phase?: string | null;
  source?: string | null;
  review_status?: string | null;
  fuel_level?: string | null;
  mileage?: number | null;
  condition_notes?: string | null;
  issues_inside?: string | null;
  issues_outside?: string | null;
  service_requests?: string[];
  oil_change_requested?: boolean;
  tire_service_requested?: boolean;
  supervisor_decision?: string | null;
  supervisor_notes?: string | null;
  reviewed_at?: string | null;
  generated_reason?: string | null;
  files?: string[];
  thumb_files?: string[];
  created_at?: string | null;
  updated_at?: string | null;
};
